/**
 * ReadinessChecklist — pre-submit gate that surfaces D1's getReadiness
 * snapshot as six concrete checks with click-through links to the failing
 * tab. Used by the rebuilt ApprovalWorkspace (D4) alongside
 * ApprovalChainBuilder (D2). The Submit button only enables when every row
 * here is green.
 *
 * The D1 service returns:
 *   { ready: boolean, reasons: string[],
 *     details: { siteCount, criterionCount, selectedSiteId } }
 *
 * `reasons` is human-readable. To render concrete per-check rows we map the
 * known reason patterns to a fixed checklist spec. The reason strings are
 * stable since they live in the same repo (D1's approvalService.js); if D1
 * adds new reasons, this map should grow with them.
 *
 * Risks check is informational only — D1 doesn't block submission for
 * missing risks (some deals legitimately have none). We render it as a
 * neutral info row.
 *
 * Linear: MYD-202 (D3).
 */

import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
	CheckCircle2,
	XCircle,
	Info,
	ChevronRight,
	Loader2,
	ShieldCheck,
	RefreshCw,
} from 'lucide-react'

import { Card, CardHeader, CardContent } from 'components/ui/card'
import { cn } from 'lib/utils'

import { fetchReadiness, type ReadinessSnapshot } from 'slices/approvalSlice'

interface ReadinessChecklistProps {
	evalId: string
	/**
	 * Bumping this counter triggers a refetch. D4 will increment it after the
	 * user navigates back to the Approvals tab so the checklist reflects any
	 * data added on Sites / Scoring / Financials / etc. without forcing the
	 * user to reload the page.
	 */
	refetchTrigger?: number
}

interface CheckSpec {
	id: 'sites' | 'selectedSite' | 'criteria' | 'scoring' | 'financials' | 'risks'
	label: string
	to: (evalId: string) => string
	tabHint: string
	// Predicate against the readiness snapshot — returns true when the check
	// PASSES. Risks is always informational so its predicate returns true
	// regardless of reasons.
	pass: (snap: ReadinessSnapshot) => boolean
	// True when this check is informational (never blocks submission).
	informational?: boolean
}

const CHECKS: CheckSpec[] = [
	{
		id: 'sites',
		label: 'At least one site',
		tabHint: 'Open the Sites tab to add a site.',
		to: (id) => `/evaluations/${id}/sites`,
		pass: (snap) =>
			!snap.reasons.some((r) => /^No sites added$/i.test(r)),
	},
	{
		id: 'selectedSite',
		label: 'A winning site is selected',
		tabHint: 'Open the Sites tab and mark the winning site.',
		to: (id) => `/evaluations/${id}/sites`,
		// Approval promotes the winning site into the Locations catalogue,
		// so the chain cannot be submitted without one.
		pass: (snap) =>
			!snap.reasons.some((r) =>
				/^No site selected$|^Selected site is no longer/i.test(r),
			),
	},
	{
		id: 'criteria',
		label: 'Scoring criteria configured',
		tabHint: 'Open the Scoring tab to add criteria with weights.',
		to: (id) => `/evaluations/${id}/scoring`,
		pass: (snap) =>
			!snap.reasons.some((r) => /^No scoring criteria added$/i.test(r)),
	},
	{
		id: 'scoring',
		label: 'Score every site against every criterion',
		tabHint: 'Open the Scoring tab to fill in missing score cells.',
		to: (id) => `/evaluations/${id}/scoring`,
		pass: (snap) => !snap.reasons.some((r) => /^Scoring incomplete/i.test(r)),
	},
	{
		id: 'financials',
		label: 'Financial model on every site',
		tabHint: 'Open the Financials tab and add a model per site.',
		to: (id) => `/evaluations/${id}/financials`,
		pass: (snap) =>
			!snap.reasons.some((r) => /missing a financial model$/i.test(r)),
	},
	{
		id: 'risks',
		label: 'Risks reviewed',
		tabHint: 'Optional — record risks if any are worth noting.',
		to: (id) => `/evaluations/${id}/risks`,
		pass: () => true,
		informational: true,
	},
]

function reasonForCheck(snap: ReadinessSnapshot, check: CheckSpec): string | null {
	// Surface the matching reason text so the row's tooltip / sub-line carries
	// the server's specific message (e.g. "Scoring incomplete (4/9 score cells)").
	const matchers: Record<CheckSpec['id'], RegExp> = {
		sites: /^No sites added$/i,
		selectedSite: /^No site selected$|^Selected site is no longer/i,
		criteria: /^No scoring criteria added$/i,
		scoring: /^Scoring incomplete/i,
		financials: /missing a financial model$/i,
		risks: /never-matches/, // informational
	}
	const re = matchers[check.id]
	return snap.reasons.find((r) => re.test(r)) ?? null
}

const ReadinessChecklist = ({ evalId, refetchTrigger }: ReadinessChecklistProps) => {
	const dispatch = useDispatch()

	const snap: ReadinessSnapshot | undefined = useSelector(
		(state: any) => state.approvals?.readinessByEvaluation?.[evalId],
	)

	useEffect(() => {
		// @ts-ignore — thunk
		dispatch(fetchReadiness(evalId))
	}, [dispatch, evalId, refetchTrigger])

	const isLoading = !snap
	const allBlockingPass = snap
		? CHECKS.filter((c) => !c.informational).every((c) => c.pass(snap))
		: false

	return (
		<Card>
			<CardHeader className="pb-3 flex-row items-center justify-between gap-3">
				<div>
					<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
						<ShieldCheck className="w-4 h-4" />
						Submission readiness
					</h4>
					<p className="text-xs text-gray-500 mt-0.5">
						Every blocking check below must pass before this evaluation can be submitted
						for approval.
					</p>
				</div>
				<button
					type="button"
					onClick={() => {
						// @ts-ignore — thunk
						dispatch(fetchReadiness(evalId))
					}}
					disabled={isLoading}
					title="Re-check readiness"
					aria-label="Re-check readiness"
					className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50"
				>
					<RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
				</button>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<div className="flex items-center gap-2 text-sm text-gray-500 py-3">
						<Loader2 className="w-4 h-4 animate-spin" />
						Checking readiness…
					</div>
				) : (
					<div className="space-y-1">
						{CHECKS.map((check) => {
							const passes = check.pass(snap)
							const isInfo = check.informational
							const reasonText = reasonForCheck(snap, check)
							const Icon = isInfo ? Info : passes ? CheckCircle2 : XCircle
							const iconClass = isInfo
								? 'text-gray-400'
								: passes
									? 'text-emerald-500'
									: 'text-red-500'
							return (
								<Link
									key={check.id}
									to={check.to(evalId)}
									className={cn(
										'flex items-center gap-3 px-3 py-2 rounded-md no-underline transition-colors',
										'hover:bg-gray-50 group',
									)}
								>
									<Icon className={cn('w-5 h-5 shrink-0', iconClass)} />
									<div className="flex-1 min-w-0">
										<div
											className={cn(
												'text-sm font-medium',
												isInfo
													? 'text-gray-700'
													: passes
														? 'text-gray-900'
														: 'text-red-700',
											)}
										>
											{check.label}
										</div>
										<div className="text-xs text-gray-500 truncate">
											{!passes && reasonText
												? reasonText
												: passes && !isInfo
													? '—'
													: check.tabHint}
										</div>
									</div>
									<ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 shrink-0" />
								</Link>
							)
						})}
					</div>
				)}

				{/* Footer: ready / not-ready summary */}
				{!isLoading && (
					<div
						className={cn(
							'mt-4 flex items-start gap-2 px-3 py-2 rounded-md text-sm',
							allBlockingPass
								? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
								: 'bg-amber-50 border border-amber-200 text-amber-800',
						)}
					>
						{allBlockingPass ? (
							<>
								<CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
								<span>
									Ready to submit. The chain becomes active when you click Submit.
								</span>
							</>
						) : (
							<>
								<XCircle className="w-4 h-4 mt-0.5 shrink-0" />
								<span>
									Not ready yet — fix the red items above before submitting.
								</span>
							</>
						)}
					</div>
				)}
			</CardContent>
		</Card>
	)
}

export { CHECKS as READINESS_CHECKS, reasonForCheck }
export default ReadinessChecklist
